import { useNavigate } from 'react-router-dom';
import { Card, Badge } from '../ui';
import type { RegisterKey } from '../../types/qms';
import type { RegisterMetrics } from '../../hooks/useRegisterMetrics';
import styles from './RegisterTile.module.css';

/**
 * RegisterTile (04-05) — one analytics tile per register.
 *
 * A `Card` showing: register name + headline count, then the secondary cuts
 * (open / overdue) with the Harmony urgency idiom (⚠️ + "N overdue" chip when
 * overdue > 0 — 04-CONTEXT.md §5, memory `user_visual_urgency_cues`; the nudge
 * IS the feature). Incidents add a reportable chip and an "awaiting a reportability decision"
 * count; Risk adds a mini rating strip (residual, else inherent rating). Every number comes from
 * `useRegisterMetrics`, which reads through `lib/registerReaders.ts` (13-16).
 *
 * Click → the matching register view (`/registers?register=<key>`) so 04-07 can
 * pre-filter to this register (deep-link-to-the-record idiom,
 * `feedback_deep_links_in_briefs`). Whole tile is a button (keyboard + a11y).
 *
 * PALETTE: purple (`--color-primary`) is IDENTITY ONLY — used here for the tile
 * accent dot / headline numeral, never as a filled interactive surface
 * (04-CONTEXT.md §5). Urgency uses the semantic warning/error tokens.
 */

/** CMA-language tile copy per register (plain, approachable — 04-CONTEXT §5). */
const TILE_META: Record<
  RegisterKey,
  { name: string; unit: string; blurb: string }
> = {
  incidents: { name: 'Incidents', unit: 'incidents', blurb: 'What happened, and what we did about it' },
  complaints: { name: 'Complaints', unit: 'complaints', blurb: 'Concerns raised, and how we’re resolving them' },
  ci: { name: 'Improvements', unit: 'improvements', blurb: 'Things we’re making better' },
  feedback: { name: 'Feedback', unit: 'feedback', blurb: 'What people are telling us' },
  risk: { name: 'Risks', unit: 'risks', blurb: 'What we’re watching and managing' },
};

/** Order risk rating buckets sensibly for the mini strip (highest first). */
const RATING_ORDER = ['Extreme', 'High', 'Medium', 'Low', 'Unrated'];

function ratingTone(rating: string): 'error' | 'warning' | 'info' | 'neutral' {
  switch (rating) {
    case 'Extreme':
    case 'High':
      return 'error';
    case 'Medium':
      return 'warning';
    case 'Low':
      return 'info';
    default:
      return 'neutral';
  }
}

interface RegisterTileProps {
  registerKey: RegisterKey;
  metrics: RegisterMetrics;
}

export default function RegisterTile({ registerKey, metrics }: RegisterTileProps) {
  const navigate = useNavigate();
  const meta = TILE_META[registerKey];
  const { total, open, overdue, reportable, awaitingDecision, ratingBuckets } = metrics;

  const go = () => navigate(`/registers?register=${registerKey}`);

  // Sort risk buckets for the mini strip; drop empty buckets.
  const ratingStrip =
    ratingBuckets &&
    RATING_ORDER.map((r) => [r, ratingBuckets[r] ?? 0] as const)
      .filter(([, n]) => n > 0)
      // include any non-standard rating values that aren't in RATING_ORDER
      .concat(
        Object.entries(ratingBuckets).filter(
          ([r]) => !RATING_ORDER.includes(r),
        ),
      );

  return (
    <Card
      variant="elevated"
      padding="md"
      className={styles.tile}
      role="button"
      tabIndex={0}
      aria-label={`${meta.name}: ${total} ${meta.unit}. Open in registers.`}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      }}
    >
      <div className={styles.head}>
        <span className={styles.dot} aria-hidden="true" />
        <span className={styles.name}>{meta.name}</span>
      </div>

      <div className={styles.headline}>
        <span className={styles.count}>{total}</span>
        <span className={styles.unit}>{total === 1 ? meta.unit.replace(/s$/, '') : meta.unit}</span>
      </div>

      <p className={styles.blurb}>{meta.blurb}</p>

      <div className={styles.cuts}>
        <span className={styles.cut}>
          <span className={styles.cutNum}>{open}</span> open
        </span>

        {overdue > 0 ? (
          <span className={styles.overdueChip}>
            {/* Overdue reads amber everywhere (D-13-15-4). */}
            <Badge variant="warning" size="sm">
              ⚠️ {overdue} overdue
            </Badge>
          </span>
        ) : (
          <span className={styles.cutMuted}>none overdue</span>
        )}
      </div>

      {/* Incidents — reportable (SIRS / NDIS / WorkSafe) and awaiting a decision. */}
      {registerKey === 'incidents' && reportable !== undefined && (
        <div className={styles.extraRow}>
          {reportable > 0 ? (
            <Badge variant="warning" size="sm">
              ⚠️ {reportable} reportable (SIRS / NDIS / WorkSafe)
            </Badge>
          ) : (
            <span className={styles.cutMuted}>None reportable</span>
          )}
          {awaitingDecision !== undefined &&
            (awaitingDecision > 0 ? (
              <Badge variant="warning" size="sm">
                {awaitingDecision} awaiting a reportability decision
              </Badge>
            ) : (
              <span className={styles.cutMuted}>None awaiting a reportability decision</span>
            ))}
        </div>
      )}

      {/* Risk — mini rating strip (heatmap data). */}
      {registerKey === 'risk' && ratingStrip && (
        <div className={styles.extraRow}>
          {ratingStrip.length > 0 ? (
            <span className={styles.ratingStrip}>
              {ratingStrip.map(([rating, n]) => (
                <Badge key={rating} variant={ratingTone(rating)} size="sm">
                  {rating} {n}
                </Badge>
              ))}
            </span>
          ) : (
            <span className={styles.cutMuted}>No risks yet</span>
          )}
        </div>
      )}
    </Card>
  );
}
